export interface RoutePlanRequest {
  waypoints: [number, number][] // [lng, lat] ordered: [start, ...intermediates, objective]
  trailCostMultiplier?: number // 0.3-1.0, default 0.5
  strategy?: RouteStrategy
}

export type RouteStrategy = 'fastest' | 'most-trail' | 'most-direct'

export const STRATEGY_TRAIL_MULTIPLIERS: Record<RouteStrategy, number> = {
  fastest: 0.5,
  'most-trail': 0.3,
  'most-direct': 0.8,
}

export interface RoutePlanWorkerInput {
  elevationGrid: Float32Array
  trailMask: Uint8Array
  landCoverGrid: Uint8Array | null
  snowDepthGrid: Float32Array | null
  gridWidth: number
  gridHeight: number
  waypoints: { col: number; row: number }[]
  cellSizeMeters: number
  trailCostMultiplier: number
  originLng: number
  originLat: number
  lngPerCol: number
  latPerRow: number
  requestId: number
}

export interface RoutePlanProgress {
  type: 'progress'
  requestId: number
  percent: number
  nodesExplored: number
  currentSegment: number
}

export interface RoutePlanComplete {
  type: 'complete'
  requestId: number
  path: Float64Array
  distanceMeters: number
  estimatedTimeMinutes: number
  elevationGainMeters: number
  elevationLossMeters: number
}

export interface RoutePlanError {
  type: 'error'
  requestId: number
  message: string
}

export type RoutePlanWorkerOutput = RoutePlanProgress | RoutePlanComplete | RoutePlanError

export interface RouteResult {
  path: [number, number][] // [lng, lat] coordinates
  distanceMeters: number
  estimatedTimeMinutes: number
  elevationGainMeters: number
  elevationLossMeters: number
  strategy: RouteStrategy
}

export const LAND_COVER_SPEED_MULTIPLIERS: Record<number, number> = {
  10: 0.4, // Tree Cover — dense bushwhacking
  20: 0.6, // Shrubland
  30: 0.9, // Grassland
  40: 0.7, // Cropland
  50: 0.8, // Built-up
  60: 0.85, // Bare / Sparse Veg
  70: 0.5, // Snow and Ice
  80: 0, // Permanent Water — impassable off-trail
  90: 0.3, // Herbaceous Wetland
  95: 0.2, // Mangroves
  100: 0.8, // Moss and Lichen
}

export function snowDepthMultiplier(depthMeters: number): number {
  if (depthMeters <= 0.15) return 1.0
  if (depthMeters <= 0.3) return 0.7
  if (depthMeters <= 0.6) return 0.5
  if (depthMeters <= 1.0) return 0.3
  return 0.15
}
